// What a star purchase costs, the same math the server charges
// (StarPurchaseService + Payments::FeeCalculator). The Support modals used to
// add fees to a total the server then charged fees on again (#2701).
import { ticketFees } from './ticketFees';

export const STAR_PRICE_CENTS = 500;

/** The seller's fee terms, from StarPurchaseService.fee_rate. */
export interface StarFeeRate {
  percent: number;
  cap_cents: number | null;
  stripe_percent: number;
  stripe_flat_cents: number;
}

export const DEFAULT_STAR_FEE: StarFeeRate = {
  percent: 0.05,
  cap_cents: null,
  stripe_percent: 0.029,
  stripe_flat_cents: 30,
};

/** Dollar amounts for the modal's itemised breakdown. */
export interface StarFeeBreakdown {
  baseCharge: number;
  platformFee: number;
  stripeFee: number;
  total: number;
  /** e.g. "Platform 3% (max $5)" */
  platformLabel: string;
  /** e.g. "Stripe 2.9% + $0.30" */
  stripeLabel: string;
}

const pct = (fraction: number) => `${+(fraction * 100).toFixed(2)}%`;
const dollars = (cents: number) => `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;

/** Fees on any gift: stars * $5, or a custom amount (#2702). */
export function giftFees(subtotalCents: number, rate: StarFeeRate = DEFAULT_STAR_FEE): StarFeeBreakdown {
  const priceCents = Math.max(Math.round(subtotalCents), 0);
  const fees = ticketFees({
    priceCents,
    platformPercent: rate.percent,
    platformCapCents: rate.cap_cents,
    absorbFees: false,
    stripe: { percent: rate.stripe_percent, flatCents: rate.stripe_flat_cents },
  });
  return {
    baseCharge: priceCents / 100,
    platformFee: fees.platformFeeCents / 100,
    stripeFee: fees.stripeFeeCents / 100,
    total: fees.buyerPaysCents / 100,
    platformLabel: `Platform ${pct(rate.percent)}${rate.cap_cents === null ? '' : ` (max ${dollars(rate.cap_cents)})`}`,
    stripeLabel: `Stripe ${pct(rate.stripe_percent)} + $${(rate.stripe_flat_cents / 100).toFixed(2)}`,
  };
}

export function starFees(stars: number, rate: StarFeeRate = DEFAULT_STAR_FEE): StarFeeBreakdown {
  return giftFees(Math.max(stars, 0) * STAR_PRICE_CENTS, rate);
}

/** The custom-amount bounds StarPurchaseService enforces. */
export const MIN_GIFT_DOLLARS = 5;
export const MAX_GIFT_DOLLARS = 5000;
